import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import FilterList, { FilterListProps } from 'core/Filters/FilterList';
import { keyBy, keys, map, without } from 'lodash';

import { useCurrentCountry } from '@lib/hooks';
import { Country } from '@lib/providers/LanguageProvider';

const messages = defineMessages({
  sms: {
    id: 'components.v2.FilterMessageChannel.sms',
    defaultMessage: 'SMS',
  },
  email: {
    id: 'components.v2.FilterMessageChannel.email',
    defaultMessage: 'Email',
  },
  voice: {
    id: 'components.v2.FilterMessageChannel.voice',
    defaultMessage: 'Voice',
  },
  inapp: {
    id: 'components.v2.FilterMessageChannel.inapp',
    defaultMessage: 'In-App',
  },
  chat: {
    id: 'components.v2.FilterMessageChannel.chat',
    defaultMessage: 'Chat',
  },
  fax: {
    id: 'components.v2.FilterMessageChannel.fax',
    defaultMessage: 'Fax',
  },
  whatsapp: {
    id: 'components.v2.FilterMessageChannel.whatsapp',
    defaultMessage: 'WhatsApp',
  },
});

export interface FilterMessageChannelProps extends Omit<FilterListProps, 'classes' | 'items' | 'allItems'> {}

const FilterMessageChannel: React.FC<FilterMessageChannelProps> = ({ ...rest }) => {
  const { formatMessage } = useIntl();
  const country = useCurrentCountry();

  const ids = useMemo(() => {
    const res = keys(messages) as (keyof typeof messages)[];

    // Hide Whatsapp channel when Localization is set to the United States
    // https://app.clickup.com/t/275azu
    if (country === Country.US) {
      return without(res, 'whatsapp');
    }
    return res;
  }, [country]);

  const items: any[] = useMemo(
    () =>
      map(ids, (id) => ({
        id,
        name: formatMessage(messages[id]),
      })),
    [ids, formatMessage],
  );

  const allItems = useMemo(() => keyBy(items, 'id'), [items]);

  return <FilterList items={items} allItems={allItems} {...rest} />;
};

export default FilterMessageChannel;
